import {Injectable} from '@angular/core'
import {map, Observable, of} from 'rxjs'
import {HttpClient} from '@angular/common/http'
import {
  ContactAddressDetail,
  ContactAddressModel,
  ContactAddressRequest,
  ContactDetailModel,
  ContactModel,
  ContactTypeModel,
  EmailModel,
  PhoneModel,
  VContactDetailModel,
  VCustomerModel,
} from "../../../domain/src/lib/contact.model"
import {environment} from "../../../../apps/frontend/src/environments/environment"
import {AddressModel} from "../../../domain/src/lib/address.model"
import {AppLogger, LoggerService} from "./logger.service"


@Injectable({
  providedIn: 'root',
})
export class ContactService {
  private readonly logger: AppLogger


  constructor(
      private readonly loggerService: LoggerService,
      private readonly http: HttpClient,
  ) {
    this.logger = this.loggerService.create('ContactService')
  }


  getContacts(
      isActivated: boolean | undefined = undefined,
      isCustomer: boolean | undefined = undefined,
  ): Observable<ContactModel[]> {
    const url = `${environment.apiUrl}/contacts?active=${isActivated}&isCustomer=${isCustomer}`
    return this.http.get<ContactModel[]>(url)
  }


  getCustomers(
      isActive: boolean | undefined = undefined,
  ): Observable<ContactModel[]> {
    const url = `${environment.apiUrl}/contacts?isCustomer=${isActive}&active=${isActive}`
    return this.http.get<ContactModel[]>(url)
  }


  /*
   getActiveCustomers(): Observable<ContactModel[]> {
   return this.getCustomers()
   }
   */

  getContactByContactId(contactId: number): Observable<ContactModel[]> {
    const url = `${environment.apiUrl}/contacts/${contactId}`
    return this.http.get<ContactModel[]>(url)
  }




  getContactAddresses(): Observable<AddressModel[]> {
    const url = `${environment.apiUrl}/contacts/addresses`
    return this.http.get<AddressModel[]>(url).pipe(
        map(values => {
          return values
        }),
    )
  }


  getContactAddressesByContactId(contactId: number): Observable<AddressModel[]> {
    const url = `${environment.apiUrl}/contacts/${contactId}/addresses`
    return this.http.get<AddressModel[]>(url).pipe(
        map(values => {
          return values
        }),
    )
  }


  /*
  getAddressReferencesByContactId(contactId: number): Observable<AddressReferenceModel[]> {
    const url = `${environment.apiUrl}/contacts/${contactId}/addresses`
    return this.http.get<AddressReferenceModel[]>(url).pipe(
        map(values => {
          return values
        }),
    )
  }
*/

  getOrderedAddressesByContactId(contactId: number): Observable<ContactAddressDetail[]> {
    const url = `${environment.apiUrl}/contacts/${contactId}/ordered-addresses`
    return this.http.get<ContactAddressDetail[]>(url).pipe(
        map(values => {
          return values
        }),
    )
  }


  getPhonesByContactId(contactId: number): Observable<PhoneModel[]> {
    const url = `${environment.apiUrl}/contacts/${contactId}/phones`
    return this.http.get<PhoneModel[]>(url).pipe(
        map(values => {
          return values
        }),
    )
  }


  addAddress(address: AddressModel): Observable<any> {
    const url = `${environment.apiUrl}/addresses`
    return this.http.post<number>(url, address).pipe(
        map(value => {
          return value
        }),
    )
  }


  addAddressByContactId(address: ContactAddressModel, description: string | null = null): Observable<any> {
    const url = `${environment.apiUrl}/contacts/addresses`
    return this.http.post<number>(url, address).pipe(
        map(value => {
          return value
        }),
    )
  }


  updateAddressByContactId(contactId: number, contactAddress: ContactAddressModel): Observable<number> {
    const url = `${environment.apiUrl}/contacts/${contactId}/addresses`
    return this.http.patch<number>(url, contactAddress)
  }


  addAddressToContact(contactId: number, addressId?: number): Observable<any> {
    const url = `${environment.apiUrl}/contacts/${contactId}/addresses/${addressId}`
    return this.http.post(url, null).pipe(
        map(value => {
          return value
        }),
    )
  }

  addContactAddress(contactAddress: ContactAddressRequest): Observable<any> {
    // const url = `${environment.apiUrl}/contacts/${contactAddress.contactId}/addresses`
    const url = `${environment.apiUrl}/contacts/addresses`
    return this.http.post(url, contactAddress).pipe(
        map(value => {
          return value
        }),
    )
  }


  getVContactAddressesByContactId(contactId: number): Observable<VContactDetailModel[]> {
    const url = `${environment.apiUrl}/contacts/${contactId}/addresses`
    return this.http.get<VContactDetailModel[]>(url).pipe(
        map(value => {
          return value
        }),
    )
  }


  getVContactDetailsByContactId(contactId: number): Observable<VContactDetailModel[]> {
    const url = `${environment.apiUrl}/contacts/${contactId}/details`
    return this.http.get<VContactDetailModel[]>(url).pipe(
        map(value => {
          return value
        }),
    )
  }


  getContactsByUserId(userId: number): Observable<ContactModel[]> {
    const url = `${environment.apiUrl}/contacts/users/${userId}`
    return this.http.get<ContactModel[]>(url)
  }


  addContact(contact: ContactModel): Observable<number> {
    const url = `${environment.apiUrl}/contacts`
    return this.http.post<number>(url, contact)
  }


  updateContact(contact: ContactModel): Observable<number> {
    const url = `${environment.apiUrl}/contacts`
    return this.http.put<number>(url, contact)
  }


  updateContactAddress(address: ContactAddressModel): Observable<number> {
    const url = `${environment.apiUrl}/contacts/addresses`
    return this.http.patch<number>(url, address)
  }


  deleteContactByContactIds(contactIds: number[]): Observable<number> {
    const url = `${environment.apiUrl}/contacts/${contactIds}`
    return this.http.delete<number>(url)
  }


  removeContactDetailByGroupAndDetailId(contactId: number, groupName: string, detailId: number): Observable<number> {
    let url = ''
    switch (groupName) {
      case 'PHONE': {
        url = `${environment.apiUrl}/contacts/${contactId}/phones/${detailId}`
        break
      }
      case 'EMAIL': {
        url = `${environment.apiUrl}/contacts/${contactId}/emails/${detailId}`
        break
      }
      case 'ADDRESS': {
        url = `${environment.apiUrl}/contacts/${contactId}/addresses/${detailId}`
        break
      }
    }
    if (url === '') return of(-1)
    return this.http.delete<number>(url)
  }


  getContactGroups(): Observable<ContactTypeModel[]> {
    const url = `${environment.apiUrl}/contacts/groups`
    return this.http.get<ContactTypeModel[]>(url)
  }


  getContactDetails(): Observable<ContactDetailModel[]> {
    const url = `${environment.apiUrl}/contacts/details`
    return this.http.get<ContactDetailModel[]>(url)
  }


  getContactDetailsByDetailId(detailId: number): Observable<ContactDetailModel[]> {
    const url = `${environment.apiUrl}/contacts/details/${detailId}`
    return this.http.get<ContactDetailModel[]>(url)
  }


  getContactDetailGroupsByGroupId(groupId: number): Observable<ContactDetailModel[]> {
    const url = `${environment.apiUrl}/contacts/details/groups/${groupId}`
    return this.http.get<ContactDetailModel[]>(url)
  }


  getContactDetailsByGroupId(groupId: number): Observable<ContactDetailModel[]> {
    const url = `${environment.apiUrl}/contacts/details/groups/${groupId}`
    return this.http.get<ContactDetailModel[]>(url)
  }


  // Email
  getEmails(): Observable<EmailModel[]> {
    const url = `${environment.apiUrl}/contacts/emails`
    return this.http.get<EmailModel[]>(url)
  }


  getEmailsByContactId(contactId: number): Observable<EmailModel[]> {
    const url = `${environment.apiUrl}/contacts/${contactId}/emails/`
    return this.http.get<EmailModel[]>(url)
  }


  addEmailByContactId(contactId: number, email: EmailModel): Observable<number> {
    const url = `${environment.apiUrl}/contacts/${contactId}/emails`
    return this.http.post<number>(url, email)
  }


  updateEmailByContactId(contactId: number, email: EmailModel): Observable<number> {
    const url = `${environment.apiUrl}/contacts/${contactId}/emails`
    return this.http.patch<number>(url, email)
  }


  // Phone
  getPhones(): Observable<PhoneModel[]> {
    const url = `${environment.apiUrl}/contacts/phones`
    return this.http.get<PhoneModel[]>(url)
  }


  addPhoneByContactId(contactId: number, phone: PhoneModel): Observable<number> {
    const url = `${environment.apiUrl}/contacts/${contactId}/phones`
    return this.http.post<number>(url, phone)
  }


  updatePhoneByContactId(contactId: number, phone: PhoneModel): Observable<number> {
    const url = `${environment.apiUrl}/contacts/${contactId}/phones`
    return this.http.patch<number>(url, phone)
  }


  getVContacts(): Observable<VContactDetailModel[]> {
    const url = `${environment.apiUrl}/contacts`
    return this.http.get<VContactDetailModel[]>(url)
  }


  getVContactsByContactId(contactId: number): Observable<VContactDetailModel[]> {
    const url = `${environment.apiUrl}/contacts/${contactId}`
    return this.http.get<VContactDetailModel[]>(url)
  }


  getVContactsByUserId(userId: number): Observable<VContactDetailModel[]> {
    const url = `${environment.apiUrl}/contacts/users/${userId}`
    return this.http.get<VContactDetailModel[]>(url)
  }


  getVCustomers(): Observable<VCustomerModel[]> {
    const url = `${environment.apiUrl}/contacts/customers`
    return this.http.get<VCustomerModel[]>(url)
  }


  getVCustomersByContactId(contactId: number): Observable<VContactDetailModel[]> {
    const url = `${environment.apiUrl}/contacts/customers/${contactId}`
    return this.http.get<VContactDetailModel[]>(url)
  }
}
